/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module VsLogsModule
 */

import { IApplicationLog, IConnectionLog } from 'generated-types';

import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tap, withLatestFrom } from 'rxjs/operators';

import { VsLogsStore } from '../../../services/vs-logs.store';
import { TVsLog, VsLogsType } from '../../../vs-logs.types';

import { DnsClientInfoPipe } from '../../../pipes/summary-content-info/dns-client-info.pipe';
import {
    DnsLoadBalancerInfoPipe,
} from '../../../pipes/summary-content-info/dns-load-balancer-info.pipe';
import { L4ClientInfoPipe } from '../../../pipes/summary-content-info/l4-client-info.pipe';
import { L7ClientInfoPipe } from '../../../pipes/summary-content-info/l7-client-info.pipe';
import { NsxAlbInfoPipe } from '../../../pipes/summary-content-info/nsx-alb-info.pipe';
import { RequestDetailsPipe } from '../../../pipes/summary-content-info/request-details.pipe';
import { RequestInfoPipe } from '../../../pipes/summary-content-info/request-info.pipe';
import { ResponseInfoPipe } from '../../../pipes/summary-content-info/response-info.pipe';
import { PolicyRulesPipe } from '../../../pipes/summary-content-info/policy-rules.pipe';
import { isObjectEmpty } from '../../../utils/vs-logs.utils';

export interface IVsLogListExpandedContentState {
    applicationLog?: IApplicationLog;
    connectionLog?: IConnectionLog;
}

const initialState: IVsLogListExpandedContentState = {
    applicationLog: {},
    connectionLog: {},
};

@Injectable()
export class VsLogListExpandedContentStore extends ComponentStore<IVsLogListExpandedContentState> {
    /**
     * Select the log passed from the parent component.
     * If defaults, n empty object is guaranteed to be returned.
     */
    public readonly vsLog$ = this.select<TVsLog>(
        ({ applicationLog, connectionLog }) => {
            return !isObjectEmpty(applicationLog) ? applicationLog : connectionLog;
        },
    );

    /**
     * Indicate whether the log has Avi defined filters.
     */
    public readonly isSignificantLog$ = this.select(this.vsLog$, ({ adf }) => adf);

    // ******************************** Generic Log Selectors ************************************

    public readonly clientRtt$ = this.select(this.vsLog$, ({ client_rtt }) => client_rtt);

    public readonly serverRtt$ = this.select(this.vsLog$, ({ server_rtt }) => server_rtt);

    public readonly totalTime$ = this.select(this.vsLog$, ({ total_time }) => (
        total_time ? Number(total_time) : total_time
    ));

    public readonly significantLog$ = this.select(
        this.vsLog$, ({ significant_log: significanceLog }) => significanceLog,
    );

    public readonly pool$ = this.select(this.vsLog$, ({ pool }) => pool);

    public readonly poolName$ = this.select(this.vsLog$, ({ pool_name }) => pool_name);

    public readonly clientIps$ = this.select(
        this.vsLog$, ({ client_ip6, client_ip }) => {
            return {
                client_ip,
                client_ip6,
            };
        },
    );

    public readonly clientSrcPort$ = this.select(
        this.vsLog$,
        ({ client_src_port }) => client_src_port,
    );

    public readonly clientDestPort$ = this.select(
        this.vsLog$,
        ({ client_dest_port }) => client_dest_port,
    );

    public readonly vsIps$ = this.select(
        this.vsLog$, ({ vs_ip, vs_ip6 }) => {
            return {
                vs_ip,
                vs_ip6,
            };
        },
    );

    public readonly serverName$ = this.select(
        this.vsLog$,
        ({ server_name }) => server_name,
    );

    public readonly serverIps$ = this.select(
        this.vsLog$, ({ server_ip, server_ip6 }) => {
            return {
                server_ip,
                server_ip6,
            };
        },
    );

    public readonly serverSrcPort$ = this.select(
        this.vsLog$, ({ server_src_port }) => server_src_port,
    );

    public readonly serverDestPort$ = this.select(
        this.vsLog$, ({ server_dest_port }) => server_dest_port,
    );

    public readonly serverConnectionSrcIps$ = this.select(
        this.vsLog$,
        ({ server_conn_src_ip, server_conn_src_ip6 }) => {
            return {
                server_conn_src_ip,
                server_conn_src_ip6,
            };
        },
    );

    public readonly serviceEngine$ = this.select(
        this.vsLog$, ({ service_engine }) => service_engine,
    );

    public readonly vcpuId$ = this.select(this.vsLog$, ({ vcpu_id }) => vcpu_id);

    // ****************************** Application Log Selectors **********************************

    public readonly responseCode$ = this.select(
        ({ applicationLog: { response_code } }) => response_code,
    );

    public readonly serverResponseCode$ = this.select(
        ({ applicationLog: { server_response_code } }) => server_response_code,
    );

    public readonly appResponseTime$ = this.select(
        ({ applicationLog: { app_response_time } }) => (
            app_response_time ? Number(app_response_time) : app_response_time
        ),
    );

    public readonly dataTransferTime$ = this.select(
        ({ applicationLog: { data_transfer_time } }) => (
            data_transfer_time ? Number(data_transfer_time) : data_transfer_time
        ),
    );

    public readonly sourceIps$ = this.select(
        ({ applicationLog: { source_ip, source_ip6 } }) => {
            return {
                source_ip,
                source_ip6,
            };
        },
    );

    public readonly l7ClientInfo$ = this.select(
        ({ applicationLog }) => this.l7ClientInfoPipe.transform(applicationLog),
    );

    public readonly nsxAlbInfo$ = this.select(
        ({ applicationLog }) => this.nsxAlbInfoPipe.transform(applicationLog),
    );

    public readonly policyRules$ = this.select(
        ({ applicationLog }) => this.policyRulesPipe.transform(applicationLog),
    );

    public readonly requestInfo$ = this.select(
        ({ applicationLog }) => this.requestInfoPipe.transform(applicationLog),
    );

    public readonly requestDetails$ = this.select(
        ({ applicationLog }) => this.requestDetailsPipe.transform(applicationLog),
    );

    public readonly responseInfo$ = this.select(
        ({ applicationLog }) => this.responseInfoPipe.transform(applicationLog),
    );

    public readonly ssl$ = this.select(
        ({ applicationLog }) => {
            const {
                ssl_version: sslVersion,
                sni_hostname: sniHostname,
                ocsp_status_resp_sent: ocspStatusRespSent,
                ssl_cipher: sslCipher,
                client_fingerprints: clientFingerprints,
                client_cipher_list: clientCipherList,
            } = applicationLog;

            const appLog = applicationLog as any;
            const sslSignatureAlgorithm = appLog.ssl_signature_algorithm;
            const sslNamedGroup = appLog.ssl_named_group;

            const ssl = {
                ssl_version: sslVersion,
                sni_hostname: sniHostname,
                ocsp_status_resp_sent: ocspStatusRespSent,
                ssl_cipher: sslCipher,
                client_fingerprints: clientFingerprints,
                client_cipher_list: clientCipherList,
                ssl_signature_algorithm: sslSignatureAlgorithm,
                ssl_named_group: sslNamedGroup,
            };

            if (isObjectEmpty(ssl)) {
                return null;
            }

            return ssl;
        },
    );

    public readonly wafLog$ = this.select(
        ({ applicationLog: { waf_log: wafLog } }) => wafLog,
    );

    public readonly botManagementLog$ = this.select(
        ({ applicationLog: { bot_management_log: botManagementLog } }) => botManagementLog,
    );

    public readonly datascriptInfo$ = this.select(
        ({ applicationLog: {
            datascript_error_trace,
            datascript_log,
        } }) => {
            const datascriptInfo = {
                datascript_error_trace,
                datascript_log,
            };

            if (isObjectEmpty(datascriptInfo)) {
                return null;
            }

            return datascriptInfo;
        },
    );

    public readonly icapLog$ = this.select(
        ({ applicationLog: { icap_log } }) => icap_log,
    );

    // true as long as one of the authentication logs is present
    public readonly hasAuthenticationLogs$ = this.select(
        ({ applicationLog: {
            saml_log,
            oauth_log,
            jwt_log,
            ntlm_log,
        } }) => Boolean(saml_log || oauth_log || jwt_log || ntlm_log),
    );

    // ************************************ L4 Log Selectors **************************************

    public readonly l4ClientInfo$ = this.select(
        ({ connectionLog }) => this.l4ClientInfoPipe.transform(connectionLog),
    );

    // ****************************** DNS Log Selectors **********************************

    public readonly dnsClientInfo$ = this.select(
        ({ connectionLog }) => this.dnsClientInfoPipe.transform(connectionLog),
    );

    public readonly dnsLoadBalancerInfo$ = this.select(
        ({ connectionLog }) => this.dnsLoadBalancerInfoPipe.transform(connectionLog),
    );

    // *************************************** Effects ****************************************

    /**
     * Update state with the host log entry, depending on the type of the log.
     */
    public readonly setLogBasedOnType = this.effect<TVsLog>(
        log$ => log$.pipe(
            withLatestFrom(this.vsLogsStore.vsLogsType$),
            tap(([log, vsLogsType]: [TVsLog, VsLogsType]) => {
                switch (log && vsLogsType) {
                    case VsLogsType.APPLICATION:
                        this.setApplicationLog(log as IApplicationLog);
                        break;

                    case VsLogsType.CONNECTION:
                        this.setConnectionLog(log as IConnectionLog);
                        break;
                }
            }),
        ),
    );

    // ****************************** private updaters **********************************

    private readonly setApplicationLog = this.updater((state, applicationLog: IApplicationLog) => ({
        ...state,
        applicationLog,
    }));

    private readonly setConnectionLog = this.updater((state, connectionLog: IApplicationLog) => ({
        ...state,
        connectionLog,
    }));

    constructor(
        private readonly vsLogsStore: VsLogsStore,
        private readonly dnsClientInfoPipe: DnsClientInfoPipe,
        private readonly dnsLoadBalancerInfoPipe: DnsLoadBalancerInfoPipe,
        private readonly l4ClientInfoPipe: L4ClientInfoPipe,
        private readonly l7ClientInfoPipe: L7ClientInfoPipe,
        private readonly policyRulesPipe: PolicyRulesPipe,
        private readonly nsxAlbInfoPipe: NsxAlbInfoPipe,
        private readonly requestInfoPipe: RequestInfoPipe,
        private readonly requestDetailsPipe: RequestDetailsPipe,
        private readonly responseInfoPipe: ResponseInfoPipe,
    ) {
        super(initialState);
    }
}
